import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DatatableComponent } from '@siemens/ngx-datatable';
import { DASHBOARD_ROUTE } from 'app/app-routing.constants';
import { LogsService } from '@shared/services/logs/logs.service';
import { SitesService } from '@shared/services/sites/sites.service';
import { Donut } from '@shared/components/highcharts/donut/donut';
import { Graph } from '@shared/components/highcharts/graph/graph';
import moment, { Moment } from 'moment';
import { AuthService } from '@shared/services/auth.service';
import { BaseDashboardComponent } from '../dashboard/baseDashboardComponent';
import { copyObject } from '@shared/utils/data-utils';
import { DateRange } from '@shared/utils/date-range';
import { ClusterUidAndName } from '@shared/models/cluster';
import { BaseLogRequest } from '@shared/models/ContextSiteModel';
import { NgFor, NgIf } from '@angular/common';
import { DonutComponent } from '@shared/components/highcharts/donut/donut.component';
import { GraphComponent } from '@shared/components/highcharts/graph/graph.component';
import { WorldMapComponent } from '@shared/components/highcharts/world-map/world-map.component';
import { BlockedIPsTableComponent } from '../dashboard/blocked-ips-table/blocked-ips-table.component';
import { SlowRequestsTableComponent } from '../dashboard/slow-requests-table/slow-requests-table.component';
import { MostFrequentRequestsTableComponent } from '../dashboard/most-frequent-requests-table/most-frequent-requests-table.component';
import { NumberFormatPipe } from '@shared/pipe/filter.pipe';

@Component({
  selector: 'app-print',
  templateUrl: './print.component.html',
  styleUrls: ['./print.component.scss'],
  imports: [
    NgIf,
    NgFor,
    DonutComponent,
    GraphComponent,
    WorldMapComponent,
    BlockedIPsTableComponent,
    SlowRequestsTableComponent,
    MostFrequentRequestsTableComponent,
    NumberFormatPipe,
    TranslatePipe,
  ],
})
export class PrintComponent extends BaseDashboardComponent implements OnInit {
  isLoading = true;
  period: DateRange;
  @ViewChild(DatatableComponent)
  ngxDatatable: DatatableComponent;
  userName = null;
  today = null;

  clusterUid: string;
  cluster: ClusterUidAndName = null;
  tags: string[] = [];
  siteNames: string[] = [];
  hasSiteFilter = false;
  startDate: string;
  endDate: string;

  get periodMoreThan7Days() {
    return this.period.length('days') >= 7;
  }

  constructor(
    http: HttpClient,
    auth: AuthService,
    sites: SitesService,
    private translate: TranslateService,
    logsService: LogsService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    graph: Graph,
    donut: Donut,
  ) {
    super(http, sites, logsService, auth, graph, donut);

    this.tableDataSize = 25;
    this.cluster = this.getRouterParam('cluster');
    this.tags = this.getRouterParam('tags');
    this.siteNames = this.getRouterParam('siteNames');
    this.hasSiteFilter = (this.siteNames?.length ?? 0) > 0;

    this.activatedRoute.queryParams.subscribe((params) => {
      this.period = new DateRange(this.parseDateTime(params.start), this.parseDateTime(params.end));
      this.clusterUid = params.clusterId ?? params.cluster;
      const organizationCode = params.organizationCode ?? params.organization;
      if (organizationCode && this.auth.currentOrganization.code != organizationCode) {
        const organization = this.auth.organizations.find((o) => o.code == organizationCode);
        if (organization) this.auth.changeCurrentOrganization(organization.code);
        else console.error(`Cannot find organization with code ${organizationCode}`);
      }
    });
  }

  async ngOnInit() {
    document.title =
      this.translate.instant('Report.Title') + '-' + this.period.start.format('L') + '_' + this.period.end.format('L');

    await this.sites.load(this.ctx);
    if (!this.siteNames || this.siteNames.length == 0) {
      await this.loadSitesForCluster(this.clusterUid);
    }

    await this.loadData();

    this.today = moment().format(this.lang == 'fr' ? 'DD/MM/YYYY HH:mm' : 'L LTS [GMT]');
    this.startDate = this.formatDateTimeGmt(this.period.start);
    this.endDate = this.formatDateTimeGmt(this.period.end);
    this.userName = `${this.auth.user.lastName} ${this.auth.user.firstName}`;

    setTimeout(() => this.print());
  }

  private print() {
    window.print();
    this.router.navigate([DASHBOARD_ROUTE]);
  }

  private formatDateTimeGmt(date: Moment) {
    return date.format(this.lang == 'fr' ? 'DD/MM/YYYY HH:mm' : 'L LTS [GMT]');
  }

  private parseDateTime(date: any): Moment {
    return typeof date === 'string' ? (/^[0-9]+$/.test(date) ? moment(parseInt(date)) : moment(date)) : moment(date);
  }

  async loadSitesForCluster(clusterUid: string) {
    const clusters = this.sites[this.ctx].store.clusters;
    if (this.clusterUid) this.cluster = clusters.find((c) => c.uid == clusterUid);

    if (!this.cluster)
      throw new Error(`Cluster not found: ${clusterUid}. Available clusters: ${clusters.map((c) => c.uid).join()}.`);

    this.siteNames = this.sites[this.ctx].store.sites.map((site) => site.domainName);
  }

  private buildRequestData(): BaseLogRequest {
    return {
      cluster: this.cluster.uid,
      tags: this.tags,
      sites: this.hasSiteFilter ? this.siteNames : null,
      fromDate: this.period.start,
      toDate: this.period.end,
    };
  }

  async loadData() {
    const request = this.buildRequestData();
    await Promise.all([this.getBlockAttack(request), this.getDateHistogram(request), this.getOrigineAttaques(request)]);

    const request7Days = copyObject(request);
    request7Days.beginDate = moment(this.period.end).subtract(7, 'days').startOf('day');

    await this.getTopConsumingRequest(request7Days);
    await this.getSlowRequest(request7Days);
    await this.getTopBlockedIps(request7Days);

    this.isLoading = false;
  }

  getRouterParam(param: string) {
    return this.router.getCurrentNavigation()?.extras?.state?.[param];
  }

  protected readonly COUNTRY_GRAPH_TYPES = ['traffic', 'attack', 'robot'] as const;
}
